/**
 * Analytics - Map Events
 * 
 * @atomic - Map interaction tracking
 * @modular - Map-specific event tracking
 * 
 * Purpose: Track map interactions, filters, and spot mode
 * Exports: trackMapInteraction, trackSearchFilterToggle, etc.
 */

import { trackEvent } from './core'

/**
 * Track map interactions
 */
export const trackMapInteraction = (action, label, customParams = {}) => {
  trackEvent('Map', action, label, null, customParams)
}

/**
 * Track map zoom events
 */
export const trackMapZoom = (zoomLevel, zoomType = 'user') => {
  trackEvent('Map', 'zoom', null, zoomLevel, { zoom_type: zoomType })
}

/**
 * Track search filter toggle
 */
export const trackSearchFilterToggle = (filterType, isActive, activeFilterCount, source) => {
  trackEvent('Map', 'filter_toggle', filterType, isActive ? 1 : 0, {
    active_filter_count: activeFilterCount,
    source: source
  })
}

/**
 * Track spot mode activation
 */
export const trackSpotModeActivation = (source = 'button') => {
  trackEvent('Map', 'spot_mode_activated', source)
}

/**
 * Track spot mode location set
 */
export const trackSpotModeLocationSet = (method, hasUserLocation, distanceFromUser, entityId) => {
  trackEvent('Map', 'spot_mode_location_set', method, null, {
    has_user_location: hasUserLocation,
    distance_from_user: distanceFromUser,
    entity_id: entityId
  })
}

/**
 * Track marker click
 */
export const trackMarkerClick = (entityId, entityName, source = 'map') => {
  trackEvent('Map', 'marker_click', entityName, null, {
    entity_id: entityId,
    source: source
  })
}

/**
 * Track spot mode activation (extended version)
 */
export const trackSpotModeActivate = (source, hasEntity, hasLocation, pageContext) => {
  trackEvent('SpotMode', 'activate', source, null, {
    pre_selected_entity: hasEntity,
    has_initial_location: hasLocation,
    page_context: pageContext
  })
}

/**
 * Track entity selected from dropdown
 */
export const trackSpotModeEntitySelected = (entityId, searchQuery, resultsCount, selectionIndex, durationSeconds) => {
  trackEvent('SpotMode', 'entity_selected', entityId, null, {
    search_query: searchQuery,
    results_count: resultsCount,
    selection_index: selectionIndex,
    time_since_activation: durationSeconds
  })
}

/**
 * Track new entity created
 */
export const trackSpotModeEntityCreated = (entityName, entityType, searchQuery, hadResults, resultsCount, durationSeconds) => {
  trackEvent('SpotMode', 'entity_created', entityName, null, {
    entity_type: entityType,
    search_query: searchQuery,
    had_search_results: hadResults,
    results_count: resultsCount,
    time_since_activation: durationSeconds
  })
}

/**
 * Track entity type changed
 */
export const trackSpotModeEntityTypeChanged = (fromType, toType) => {
  trackEvent('SpotMode', 'entity_type_changed', null, null, {
    from_type: fromType,
    to_type: toType
  })
}

/**
 * Track validation failure
 */
export const trackSpotModeValidationFailed = (reason, hasEntity, hasLocation, imageCount, durationSeconds) => {
  trackEvent('SpotMode', 'validation_failed', reason, null, {
    failure_reason: reason,
    has_entity: hasEntity,
    has_location: hasLocation,
    image_count: imageCount,
    time_since_activation: durationSeconds
  })
}

/**
 * Track submit attempt (before API call)
 */
export const trackSpotModeSubmitAttempt = (entityId, isNewEntity, entityType, hasNotes, imageCount, durationSeconds, locationDuration) => {
  trackEvent('SpotMode', 'submit_attempt', entityId, null, {
    is_new_entity: isNewEntity,
    entity_type: entityType,
    has_notes: hasNotes,
    image_count: imageCount,
    time_since_activation: durationSeconds,
    time_since_location_set: locationDuration
  })
}

/**
 * Track submit failure (API error)
 */
export const trackSpotModeSubmitFailed = (errorMessage, errorType, wasNewEntity, durationSeconds) => {
  trackEvent('SpotMode', 'submit_failed', null, null, {
    error_message: errorMessage,
    error_type: errorType,
    was_new_entity: wasNewEntity,
    time_since_activation: durationSeconds
  })
}

/**
 * Track spot mode cancellation
 */
export const trackSpotModeCancel = (hadLocation, hadEntity, hadNotes, imageCount, durationSeconds, stage) => {
  trackEvent('SpotMode', 'cancel', stage, null, {
    had_location: hadLocation,
    had_entity: hadEntity,
    had_notes: hadNotes,
    image_count: imageCount,
    time_since_activation: durationSeconds,
    cancellation_stage: stage
  })
}

/**
 * Track image added
 */
export const trackSpotModeImageAdded = (imageCount, fileSizeKb, fileType, isPrimary) => {
  trackEvent('SpotMode', 'image_added', null, null, {
    image_count: imageCount,
    file_size_kb: fileSizeKb,
    file_type: fileType,
    is_primary: isPrimary
  })
}

/**
 * Track image removed
 */
export const trackSpotModeImageRemoved = (remainingImages, wasPrimary) => {
  trackEvent('SpotMode', 'image_removed', null, null, {
    remaining_images: remainingImages,
    was_primary: wasPrimary
  })
}

/**
 * Track image upload failure
 */
export const trackSpotModeImageUploadFailed = (reason, fileSizeKb, fileType, currentImageCount) => {
  trackEvent('SpotMode', 'image_upload_failed', reason, null, {
    failure_reason: reason,
    file_size_kb: fileSizeKb,
    file_type: fileType,
    current_image_count: currentImageCount
  })
}

/**
 * Track spot mode reset
 */
export const trackSpotModeReset = (hadEntity, hadNotes, imageCount) => {
  trackEvent('SpotMode', 'reset', null, null, {
    had_entity: hadEntity,
    had_notes: hadNotes,
    image_count: imageCount
  })
}

/**
 * Track spot mode panel minimized
 */
export const trackSpotModePanelMinimized = (completionStage) => {
  trackEvent('SpotMode', 'panel_minimized', completionStage)
}

/**
 * Track auth gate hit — unauthenticated user attempted to submit a sighting.
 * Fires when the submit path redirects to login instead of processing.
 * Useful for measuring authenticated conversion rate out of SpotMode starts.
 */
export const trackSpotModeAuthGate = (hasEntity, hasLocation, hasImages, entityType) => {
  trackEvent('SpotMode', 'auth_gate_hit', entityType || 'unknown', null, {
    has_entity: hasEntity,
    has_location: hasLocation,
    has_images: hasImages,
    entity_type: entityType,
  })
}

/**
 * Track draft restored after login (regular or OAuth redirect).
 * Fires when the restore effect successfully re-populates SpotMode state.
 * method: 'session' (in-memory draft from same tab login) | 'indexeddb' (OAuth redirect)
 */
export const trackSpotModeDraftRestored = (method, hasEntity, hasLocation, hasImages, hasNotes) => {
  trackEvent('SpotMode', 'draft_restored', method, null, {
    restore_method: method,
    has_entity: hasEntity,
    has_location: hasLocation,
    has_images: hasImages,
    has_notes: hasNotes,
  })
}

/**
 * Track NSFW image rejection — upload returned 422 content policy error.
 * Fires instead of trackSpotModeImageUploadFailed so the two can be differentiated.
 */
export const trackSpotModeImageNsfwRejected = (fileType, fileSizeKb, currentImageCount) => {
  trackEvent('SpotMode', 'image_nsfw_rejected', fileType || 'unknown', null, {
    file_type: fileType,
    file_size_kb: fileSizeKb,
    current_image_count: currentImageCount,
  })
}
